import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FaArrowLeft,
  FaShieldAlt,
  FaGlobe,
  FaHandshake,
  FaFutbol,
  FaShoppingBag,
  FaTshirt,
  FaCheckCircle,
  FaCheck,
  FaTrophy,
  FaBullseye,
  FaGlobeAmericas,
  FaArrowRight,
  FaStar,
  FaCogs,
  FaShippingFast,
  FaAward,
  FaUsers,
  FaChartLine
} from 'react-icons/fa';

import {
  GiBoxingGlove,
  GiSewingNeedle,
  GiFactory,
  GiTeamIdea,
  GiSoccerBall
} from 'react-icons/gi';


/* ============================================================
   LOCAL IMAGE IMPORTS
   ============================================================ */

import reko1 from '../logos/rekosports/rekosportlogo.png';
import reko2 from '../logos/rekosports/reko1.png';
import reko3 from '../logos/rekosports/reko2.png';
import reko4 from '../logos/rekosports/reko3.png';
import reko5 from '../logos/rekosports/reko4.png';
import reko6 from '../logos/rekosports/reko5.png';
import reko7 from '../logos/rekosports/reko6.png';
import reko8 from '../logos/rekosports/reko7.png';


/* ============================================================
   MAIN COMPONENT
   ============================================================ */

export default function RekoSports() {

  const navigate = useNavigate();
  const pageRef = useRef(null);


  /* ============================================================
     SCROLL REVEAL
     ============================================================ */

  useEffect(() => {

    const page = pageRef.current;

    if (!page) return;

    const elements = page.querySelectorAll('.reko-reveal');

    const observer = new IntersectionObserver(
      (entries) => {

        entries.forEach((entry) => {

          if (entry.isIntersecting) {

            entry.target.classList.add('reko-visible');

            observer.unobserve(entry.target);

          }

        });

      },
      {
        threshold: 0.08
      }
    );

    elements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };

  }, []);


  return (

    <div
      ref={pageRef}
      className="reko-page"
      style={styles.page}
    >

      {/* ======================================================
          GLOBAL CSS
      ====================================================== */}

      <style>{`

        * {
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        body {
          margin: 0;
          padding: 0;
          overflow-x: hidden;
        }

        button {
          font-family: inherit;
        }

        img {
          max-width: 100%;
        }


        /* ====================================================
           PAGE
        ==================================================== */

        .reko-page {
          width: 100%;
          min-height: 100vh;
          overflow-x: hidden;
          background:
            radial-gradient(
              circle at 15% 10%,
              rgba(24,110,82,.18),
              transparent 30%
            ),
            radial-gradient(
              circle at 90% 40%,
              rgba(12,74,56,.12),
              transparent 35%
            ),
            #11181a;
        }


        /* ====================================================
           REVEAL
        ==================================================== */

        .reko-reveal {
          opacity: 0;
          transform: translateY(45px);
          transition:
            opacity .9s ease,
            transform .9s cubic-bezier(.2,.8,.2,1);
        }

        .reko-visible {
          opacity: 1;
          transform: translateY(0);
        }


        /* ====================================================
           POSTER
        ==================================================== */

        .poster-card {
          animation: posterEnter .9s ease-out both;
        }

        @keyframes posterEnter {

          from {
            opacity: 0;
            transform: translateY(25px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }

        }


        /* ====================================================
           BACK BUTTON
        ==================================================== */

        .reko-back-button {
          transition:
            transform .3s ease,
            background .3s ease,
            box-shadow .3s ease;
        }

        .reko-back-button:hover {
          transform: translateX(-5px);
          background: #087050 !important;
          box-shadow:
            0 10px 25px rgba(0,0,0,.28);
        }


        /* ====================================================
           HERO
        ==================================================== */

        .reko-hero-left {
          animation:
            heroLeft 1s ease-out .15s both;
        }

        .reko-hero-right {
          animation:
            heroRight 1s ease-out .3s both;
        }

        @keyframes heroLeft {

          from {
            opacity: 0;
            transform: translateX(-50px);
          }

          to {
            opacity: 1;
            transform: translateX(0);
          }

        }

        @keyframes heroRight {

          from {
            opacity: 0;
            transform: translateX(50px) scale(.97);
          }

          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }

        }


        /* ====================================================
           LOGO
        ==================================================== */

        .reko-logo-image {
          animation:
            logoAppear 1s ease-out .3s both,
            logoFloat 5s ease-in-out 1.5s infinite;
        }

        @keyframes logoAppear {

          0% {
            opacity: 0;
            transform: scale(.65) rotate(-5deg);
          }

          70% {
            opacity: 1;
            transform: scale(1.04) rotate(1deg);
          }

          100% {
            opacity: 1;
            transform: scale(1);
          }

        }

        @keyframes logoFloat {

          0%,
          100% {
            transform: translateY(0);
          }

          50% {
            transform: translateY(-5px);
          }

        }


        /* ====================================================
           MAIN IMAGE
        ==================================================== */

        .reko-main-image {
          transition:
            transform .9s cubic-bezier(.2,.8,.2,1),
            filter .6s ease;
        }

        .reko-hero-right:hover .reko-main-image {
          transform: scale(1.045);
          filter: brightness(1.08);
        }


        /* ====================================================
           COLLAGE
        ==================================================== */

        .reko-collage {
          transition:
            transform .35s ease,
            box-shadow .35s ease;
        }

        .reko-collage:hover {
          transform:
            scale(1.07)
            translateX(-4px);

          box-shadow:
            0 18px 35px rgba(0,0,0,.35);

          z-index: 30;
        }


        /* ====================================================
           ICONS
        ==================================================== */

        .reko-icon {
          transition:
            transform .3s ease,
            box-shadow .3s ease,
            background .3s ease,
            color .3s ease;
        }

        .reko-icon:hover {
          transform:
            translateY(-6px)
            scale(1.08);

          box-shadow:
            0 10px 24px rgba(12,74,56,.22);
        }


        /* ====================================================
           STATS
        ==================================================== */

        .reko-stat-card {
          transition:
            transform .35s ease,
            box-shadow .35s ease,
            border-color .35s ease;
        }

        .reko-stat-card:hover {
          transform: translateY(-6px);

          box-shadow:
            0 15px 30px rgba(0,0,0,.10);

          border-color:
            #0c4a38 !important;
        }


        /* ====================================================
           PRODUCTS
        ==================================================== */

        .reko-product-card {
          transition:
            transform .35s ease,
            box-shadow .35s ease,
            background .35s ease;
        }

        .reko-product-card:hover {
          transform: translateY(-6px);

          box-shadow:
            0 15px 30px rgba(0,0,0,.10);

          background:
            #f8fcfa !important;
        }

        .reko-product-icon {
          transition:
            transform .35s ease,
            background .35s ease,
            color .35s ease;
        }

        .reko-product-card:hover
        .reko-product-icon {
          transform:
            rotate(-6deg)
            scale(1.08);

          background:
            #0c4a38 !important;

          color:
            #fff !important;
        }


        /* ====================================================
           STRENGTH
        ==================================================== */

        .reko-strength-card {
          transition:
            transform .35s ease,
            box-shadow .35s ease;
        }

        .reko-strength-card:hover {
          transform: translateY(-7px);

          box-shadow:
            0 15px 28px rgba(0,0,0,.08);
        }


        /* ====================================================
           CAPABILITY
        ==================================================== */

        .reko-capability-row {
          transition:
            transform .3s ease,
            background .3s ease;
        }

        .reko-capability-row:hover {
          transform: translateX(5px);

          background:
            #f5faf8 !important;
        }


        /* ====================================================
           FACTORY
        ==================================================== */

        .reko-factory-image {
          transition:
            transform .8s ease,
            filter .5s ease;
        }

        .reko-factory-wrapper:hover
        .reko-factory-image {
          transform: scale(1.06);
          filter: brightness(1.06);
        }


        /* ====================================================
           WORLD MAP
        ==================================================== */

        .reko-world-map {
          animation:
            worldFloat 5s ease-in-out infinite;
        }

        @keyframes worldFloat {

          0%,
          100% {
            transform: translateY(0);
          }

          50% {
            transform: translateY(-5px);
          }

        }


        /* ====================================================
           SHIP
        ==================================================== */

        .reko-ship-image {
          transition:
            transform .8s ease,
            filter .5s ease;
        }

        .reko-ship-wrapper:hover
        .reko-ship-image {
          transform: scale(1.06);
          filter: brightness(1.07);
        }


        /* ====================================================
           FOOTER PILLAR
        ==================================================== */

        .reko-pillar {
          transition:
            transform .3s ease;
        }

        .reko-pillar:hover {
          transform: translateY(-5px);
        }


        /* ====================================================
           MOVE TO OTHER SECTORS
        ==================================================== */

        .sector-button {
          transition:
            transform .3s ease,
            box-shadow .3s ease,
            background .3s ease;
        }

        .sector-button:hover {
          transform: translateY(-4px);
          box-shadow:
            0 12px 28px rgba(0,0,0,.25);
          background: #eaf5f0 !important;
        }


        /* ====================================================
           TABLET
        ==================================================== */

        @media(max-width:1000px) {

          .poster-card {
            padding:
              30px 4% !important;
          }

          .hero-headline {
            font-size:
              34px !important;
          }

          .hero-left {
            padding-right:
              18px !important;
          }

          .reko-logo-image {
            width:
              135px !important;
          }

          .brand-name {
            font-size:
              21px !important;
          }

          .product-image-box {
            width:
              155px !important;
          }

          .worldwide-section {
            grid-template-columns:
              1fr 1.3fr !important;
          }

          .world-intro {
            grid-column:
              1 / 2 !important;
          }

          .world-map-container {
            grid-column:
              2 / 3 !important;
          }

          .markets-column {
            grid-column:
              1 / 2 !important;
          }

          .ship-banner-wrapper {
            grid-column:
              2 / 3 !important;
          }

          .sector-navigation-inner {
            gap: 20px !important;
          }

          .sector-title {
            font-size:
              25px !important;
          }

        }


        /* ====================================================
           MOBILE
        ==================================================== */

        @media(max-width:700px) {

          .top-nav {
            padding:
              12px 15px !important;
          }

          .poster-card {
            padding:
              22px 15px !important;
          }

          .reko-hero-section {
            flex-direction:
              column !important;

            min-height:
              auto !important;
          }

          .hero-left {
            width:
              100% !important;

            padding:
              5px 0 25px !important;
          }

          .hero-right {
            width:
              100% !important;

            height:
              340px !important;

            min-height:
              340px !important;
          }


          /* LOGO */

          .logo-area {
            flex-direction:
              column !important;

            align-items:
              center !important;

            text-align:
              center !important;

            gap:
              4px !important;
          }

          .reko-logo-image {
            width:
              155px !important;
          }

          .brand-name {
            font-size:
              25px !important;
          }

          .brand-tagline {
            font-size:
              6.5px !important;
          }


          /* HERO */

          .hero-title-box {
            text-align:
              center !important;
          }

          .hero-eyebrow {
            justify-content:
              center !important;
          }

          .hero-headline {
            font-size:
              29px !important;
          }

          .hero-description {
            font-size:
              10.5px !important;

            margin-left:
              auto !important;

            margin-right:
              auto !important;
          }

          .hero-badges-grid {
            gap:
              4px !important;
          }

          .badge-icon-circle {
            width:
              32px !important;

            height:
              32px !important;
          }

          .badge-text {
            font-size:
              6.2px !important;
          }


          /* COLLAGE */

          .reko-collage {
            width:
              102px !important;

            height:
              68px !important;

            right:
              7px !important;
          }

          .top-right-collage {
            top:
              8px !important;
          }

          .mid-right-collage {
            top:
              82px !important;
          }

          .bot-right-collage {
            top:
              156px !important;
          }

          .quality-banner {
            left:
              4% !important;

            padding:
              9px 12px !important;
          }

          .hero-corner-label {
            left:
              10px !important;

            top:
              10px !important;
          }


          /* STATS */

          .stats-section {
            grid-template-columns:
              repeat(2,1fr) !important;
          }


          /* TWO COLUMNS */

          .two-column-section {
            flex-direction:
              column !important;

            gap:
              28px !important;
          }

          .about-column,
          .product-range-column,
          .strengths-column,
          .capabilities-column,
          .factory-column {
            width:
              100% !important;
          }


          /* PRODUCTS */

          .product-area {
            flex-direction:
              column !important;
          }

          .product-image-box {
            width:
              100% !important;

            height:
              230px !important;
          }


          /* STRENGTHS */

          .strengths-grid {
            grid-template-columns:
              repeat(5,1fr) !important;

            gap:
              3px !important;
          }

          .strength-card {
            padding:
              10px 2px !important;
          }

          .strength-icon {
            width:
              32px !important;

            height:
              32px !important;

            font-size:
              12px !important;
          }

          .strength-card strong {
            font-size:
              6px !important;
          }

          .strength-card span {
            font-size:
              5.5px !important;
          }


          /* FACTORY */

          .factory-column {
            width:
              100% !important;
          }

          .factory-image-wrapper {
            width:
              100% !important;

            min-height:
              210px !important;
          }


          /* WORLD */

          .worldwide-section {
            display:
              flex !important;

            flex-direction:
              column !important;

            gap:
              22px !important;
          }

          .world-map-container {
            width:
              100% !important;

            height:
              180px !important;
          }

          .markets-column {
            width:
              100% !important;
          }

          .ship-banner-wrapper {
            width:
              100% !important;

            height:
              190px !important;
          }


          /* CTA */

          .cta-section {
            flex-direction:
              column !important;

            align-items:
              flex-start !important;

            padding:
              22px !important;
          }

          .cta-button {
            width:
              100% !important;

            justify-content:
              center !important;
          }


          /* MOVE TO SECTORS */

          .sector-navigation {
            margin-left:
              -15px !important;

            margin-right:
              -15px !important;

            width:
              calc(100% + 30px) !important;
          }

          .sector-navigation-inner {
            flex-direction:
              column !important;

            align-items:
              stretch !important;

            text-align:
              center !important;
          }

          .sector-text {
            margin-left:
              auto !important;

            margin-right:
              auto !important;
          }

          .sector-button {
            width:
              100% !important;
          }


          /* FOOTER */

          .reko-footer {
            flex-direction:
              column !important;

            text-align:
              center !important;

            gap:
              18px !important;
          }

          .footer-pillars {
            width:
              100% !important;

            justify-content:
              space-between !important;

            gap:
              8px !important;
          }

          .pillar-item {
            flex:
              1 !important;

            flex-direction:
              column !important;

            text-align:
              center !important;
          }

          .footer-bottom {
            flex-direction:
              column !important;

            gap:
              5px !important;

            text-align:
              center !important;
          }

        }


        /* ====================================================
           SMALL MOBILE
        ==================================================== */

        @media(max-width:420px) {

          .poster-card {
            padding:
              18px 10px !important;
          }

          .hero-right {
            height:
              290px !important;

            min-height:
              290px !important;
          }

          .hero-headline {
            font-size:
              24px !important;
          }

          .reko-collage {
            width:
              86px !important;

            height:
              58px !important;
          }

          .mid-right-collage {
            top:
              65px !important;
          }

          .bot-right-collage {
            top:
              122px !important;
          }

          .banner-small {
            font-size:
              6px !important;
          }

          .banner-large {
            font-size:
              9px !important;
          }

          .strength-label {
            font-size:
              5px !important;
          }

          .sector-title {
            font-size:
              22px !important;
          }

        }


        /* ====================================================
           REDUCED MOTION
        ==================================================== */

        @media(prefers-reduced-motion:reduce) {

          *,
          *::before,
          *::after {
            animation-duration:
              .01ms !important;

            animation-iteration-count:
              1 !important;

            transition-duration:
              .01ms !important;

            scroll-behavior:
              auto !important;
          }

        }

      `}</style>


      {/* ======================================================
          TOP NAVIGATION
      ====================================================== */}

      <div
        style={styles.topNav}
        className="top-nav"
      >

        <button
          style={styles.backButton}
          className="reko-back-button"
          onClick={() => navigate('/')}
        >

          <FaArrowLeft />

          <span style={{ marginLeft: 8 }}>
            Back to All Sectors
          </span>

        </button>

      </div>


      {/* ======================================================
          MAIN WEBSITE
      ====================================================== */}

      <main
        style={styles.posterCard}
        className="poster-card"
      >


        {/* ====================================================
            HERO
        ==================================================== */}

        <section
          style={styles.heroSection}
          className="reko-hero-section"
        >


          {/* LEFT */}

          <div
            style={styles.heroLeft}
            className="hero-left reko-hero-left"
          >

            <div>

              <div
                style={styles.logoArea}
                className="logo-area"
              >

                <img
                  src={reko1}
                  alt="REKO Sports Logo"
                  style={styles.logoImage}
                  className="reko-logo-image"
                />

                {/* TEXT BELOW LOGO */}

                <div style={styles.brandText}>

                  <div
                    style={styles.brandName}
                    className="brand-name"
                  >
                    REKO SPORTS
                  </div>

                  <div
                    style={styles.brandTagline}
                    className="brand-tagline"
                  >
                    SPORTS MANUFACTURING & GLOBAL EXPORT
                  </div>

                </div>

              </div>


              <div
                style={styles.heroTitleBox}
                className="hero-title-box"
              >

                <div
                  style={styles.heroEyebrow}
                  className="hero-eyebrow"
                >

                  <span
                    style={styles.heroEyebrowLine}
                  />

                  PERFORMANCE ENGINEERED

                </div>


                <h1
                  style={styles.heroHeadline}
                  className="hero-headline"
                >

                  BUILT FOR
                  <br />

                  PERFORMANCE.
                  <br />

                  <span
                    style={{
                      color: '#0c4a38'
                    }}
                  >
                    MADE TO WIN.
                  </span>

                </h1>


                <p
                  style={styles.heroDescription}
                  className="hero-description"
                >

                  REKO Sports is the dedicated sports
                  manufacturing division of Ashraf Group of
                  Industries, focused on producing high-quality
                  sports goods for athletes, professional teams,
                  retailers, distributors, and international brands.

                </p>

                <p
                  style={styles.heroDescription}
                  className="hero-description"
                >

                  From performance-driven equipment to
                  customized sportswear and accessories, we
                  combine skilled craftsmanship, modern
                  manufacturing, strict quality control, and
                  dependable global supply capabilities.

                </p>

              </div>

            </div>


            {/* BADGES */}

            <div
              style={styles.heroBadgesGrid}
              className="hero-badges-grid"
            >

              <Badge
                icon={<FaShieldAlt />}
                top="PREMIUM"
                bottom="QUALITY"
              />

              <Badge
                icon={<GiSewingNeedle />}
                top="EXPERT"
                bottom="CRAFTSMANSHIP"
              />

              <Badge
                icon={<FaGlobe />}
                top="GLOBAL"
                bottom="STANDARDS"
              />

              <Badge
                icon={<FaHandshake />}
                top="TRUSTED"
                bottom="PARTNER"
              />

            </div>

          </div>


          {/* RIGHT */}

          <div
            style={styles.heroRight}
            className="hero-right reko-hero-right"
          >

            <div style={styles.heroImageFrame}>

              <img
                src={reko2}
                alt="REKO Sports Performance"
                style={styles.heroMainImg}
                className="reko-main-image"
              />

            </div>


            <Collage
              className="top-right-collage"
              style={styles.topRightCollage}
              image={reko3}
              alt="REKO Football"
            />


            <Collage
              className="mid-right-collage"
              style={styles.midRightCollage}
              image={reko4}
              alt="REKO Boxing"
            />


            <Collage
              className="bot-right-collage"
              style={styles.botRightCollage}
              image={reko5}
              alt="REKO Manufacturing"
            />


            <div
              style={styles.greenBanner}
              className="quality-banner"
            >

              <span
                style={styles.bannerSmall}
                className="banner-small"
              >
                QUALITY YOU CAN FEEL.
              </span>

              <strong
                style={styles.bannerLarge}
                className="banner-large"
              >
                PERFORMANCE YOU CAN TRUST.
              </strong>

            </div>


            <div
              style={styles.heroCornerLabel}
              className="hero-corner-label"
            >

              <GiSoccerBall />

              <span>
                MADE FOR
                <br />
                THE GAME
              </span>

            </div>

          </div>

        </section>


        {/* ====================================================
            STATS
        ==================================================== */}

        <section
          style={styles.statsSection}
          className="reko-reveal stats-section"
        >

          <Stat
            icon={<GiFactory />}
            number="01"
            label="MODERN"
            sub="MANUFACTURING"
          />

          <Stat
            icon={<FaAward />}
            number="02"
            label="QUALITY"
            sub="FOCUSED"
          />

          <Stat
            icon={<FaUsers />}
            number="03"
            label="SKILLED"
            sub="WORKFORCE"
          />

          <Stat
            icon={<FaGlobe />}
            number="04"
            label="GLOBAL"
            sub="REACH"
          />

        </section>


        {/* ====================================================
            ABOUT + PRODUCTS
        ==================================================== */}

        <section
          style={styles.twoColumnSection}
          className="two-column-section reko-reveal"
        >

          {/* ABOUT */}

          <div
            style={styles.aboutColumn}
            className="about-column"
          >

            <SectionTitle
              eyebrow="WHO WE ARE"
              title="ABOUT"
              accent="REKO SPORTS"
            />


            <p style={styles.paragraphText}>

              REKO Sports specializes in the manufacturing
              and international export of a comprehensive
              range of sports goods. Our product portfolio
              includes sports balls, gloves, bags, apparel,
              training equipment, and performance accessories.

            </p>


            <p style={styles.paragraphText}>

              We work with customers who demand consistent
              quality, dependable production, competitive
              pricing, attractive finishing, and reliable
              delivery. Our manufacturing approach combines
              practical industry experience with modern
              production techniques.

            </p>


            <p style={styles.paragraphText}>

              Whether the requirement is a standard product,
              private-label collection, customized design,
              OEM production, or ODM development, REKO Sports
              is structured to deliver flexible solutions for
              different international markets.

            </p>


            <div style={styles.missionBox}>

              <div style={styles.missionIcon}>
                <FaBullseye />
              </div>

              <div>

                <strong style={styles.missionTitle}>
                  OUR MISSION
                </strong>

                <p style={styles.missionText}>
                  To empower athletes, teams, retailers,
                  distributors, and sports brands with
                  dependable products engineered for
                  performance, durability, and everyday use.
                </p>

              </div>

            </div>


            <div style={styles.aboutHighlights}>

              <div style={styles.aboutHighlight}>
                <FaCheckCircle />
                <span>International Quality Standards</span>
              </div>

              <div style={styles.aboutHighlight}>
                <FaCheckCircle />
                <span>Reliable Production Capability</span>
              </div>

              <div style={styles.aboutHighlight}>
                <FaCheckCircle />
                <span>Custom OEM & ODM Solutions</span>
              </div>

              <div style={styles.aboutHighlight}>
                <FaCheckCircle />
                <span>Global Export Orientation</span>
              </div>

            </div>

          </div>


          {/* PRODUCTS */}

          <div
            style={styles.productRangeColumn}
            className="product-range-column"
          >

            <SectionTitle
              eyebrow="WHAT WE MAKE"
              title="OUR"
              accent="PRODUCT RANGE"
            />


            <div style={styles.productArea}>

              <div style={styles.productListContainer}>

                <ProductCard
                  icon={<FaFutbol />}
                  title="SPORTS BALLS"
                  description="Football, volleyball, basketball, handball and customized sports balls manufactured for different performance requirements."
                />

                <ProductCard
                  icon={<GiBoxingGlove />}
                  title="GLOVES"
                  description="Boxing, training, goalkeeper, batting and customized gloves designed for comfort, protection and performance."
                />

                <ProductCard
                  icon={<FaShoppingBag />}
                  title="SPORTS BAGS"
                  description="Sports bags, backpacks, kit bags, duffle bags and travel solutions for teams, athletes and retailers."
                />

                <ProductCard
                  icon={<FaTshirt />}
                  title="APPAREL & ACCESSORIES"
                  description="Sportswear, jerseys, tracksuits, caps, socks and accessories for teams, clubs, brands and private-label programs."
                />

              </div>


              <div
                style={styles.productImageBox}
                className="product-image-box"
              >

                <img
                  src={reko6}
                  alt="REKO Sports Product Range"
                  style={styles.productImage}
                  className="reko-main-image"
                />

                <div style={styles.productImageOverlay}>

                  <span>
                    REKO
                  </span>

                  <strong>
                    SPORTS
                  </strong>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* ====================================================
            STRENGTHS
        ==================================================== */}

        <section
          style={styles.strengthSection}
          className="reko-reveal"
        >

          <SectionTitle
            eyebrow="WHY REKO"
            title="OUR"
            accent="STRENGTHS"
            centered
          />


          <div
            style={styles.strengthsGrid}
            className="strengths-grid"
          >

            <Strength
              icon={<FaCheckCircle />}
              title="HIGH QUALITY"
              sub="MATERIALS"
            />

            <Strength
              icon={<GiFactory />}
              title="ADVANCED"
              sub="MANUFACTURING"
            />

            <Strength
              icon={<GiTeamIdea />}
              title="SKILLED"
              sub="WORKFORCE"
            />

            <Strength
              icon={<FaShieldAlt />}
              title="STRICT QUALITY"
              sub="CONTROL"
            />

            <Strength
              icon={<FaShippingFast />}
              title="ON-TIME"
              sub="DELIVERY"
            />

          </div>

        </section>


        {/* ====================================================
            CAPABILITIES + FACTORY
        ==================================================== */}

        <section
          style={styles.twoColumnSection}
          className="two-column-section reko-reveal"
        >

          <div
            style={styles.capabilitiesColumn}
            className="capabilities-column"
          >

            <SectionTitle
              eyebrow="WHAT WE OFFER"
              title="OUR"
              accent="CAPABILITIES"
            />


            <div style={styles.capabilitiesList}>

              <Capability
                icon={<FaCogs />}
                text="Custom Design & Branding"
              />

              <Capability
                icon={<FaHandshake />}
                text="OEM & ODM Services"
              />

              <Capability
                icon={<GiFactory />}
                text="Bulk Production Capacity"
              />

              <Capability
                icon={<FaChartLine />}
                text="Competitive International Pricing"
              />

              <Capability
                icon={<FaShippingFast />}
                text="Reliable & Timely Shipments"
              />

              <Capability
                icon={<FaUsers />}
                text="Dedicated Customer Support"
              />

            </div>


            <div style={styles.capabilityBottomNote}>

              <FaStar />

              <span>
                Flexible manufacturing solutions tailored
                to international sports brands, distributors,
                retailers and private-label customers.
              </span>

            </div>

          </div>


          {/* FACTORY */}

          <div
            style={styles.factoryColumn}
            className="factory-column"
          >

            <div
              style={styles.factoryImageWrapper}
              className="reko-factory-wrapper"
            >

              <img
                src={reko7}
                alt="REKO Sports Manufacturing Facility"
                style={styles.factoryImage}
                className="reko-factory-image"
              />

              <div style={styles.factoryOverlay}>

                <span>
                  MANUFACTURING
                </span>

                <strong>
                  BUILT TO PERFORM
                </strong>

              </div>

            </div>

          </div>

        </section>


        {/* ====================================================
            WORLDWIDE
        ==================================================== */}

        <section
          style={styles.worldwideSection}
          className="worldwide-section reko-reveal"
        >

          <div style={styles.worldIntro}>

            <div style={styles.worldEyebrow}>
              GLOBAL PRESENCE
            </div>

            <h2 style={styles.worldTitle}>

              DELIVERING
              <br />

              <span style={{ color: '#0c4a38' }}>
                WORLDWIDE
              </span>

            </h2>

            <p style={styles.worldText}>

              REKO Sports products are manufactured with
              international quality requirements in mind
              and supplied to customers across multiple
              global markets.

            </p>

            <div style={styles.globalBadge}>

              <FaGlobe />

              GLOBAL EXPORT

            </div>

          </div>


          {/* MAP */}

          <div
            style={styles.worldMapContainer}
            className="world-map-container"
          >

            <img
              src={reko8}
              alt="REKO Global Markets"
              style={styles.worldMapImage}
              className="reko-world-map"
            />

          </div>


          {/* MARKETS */}

          <div style={styles.marketsColumn}>

            <div style={styles.marketHeading}>

              <FaGlobe />

              OUR GLOBAL MARKETS

            </div>


            <div style={styles.marketGrid}>

              <Market text="EUROPE" />
              <Market text="MIDDLE EAST" />
              <Market text="NORTH AMERICA" />
              <Market text="SOUTH AMERICA" />
              <Market text="AFRICA" />
              <Market text="ASIA & OCEANIA" />

            </div>

          </div>


          {/* SHIPPING */}

          <div
            style={styles.shipBanner}
            className="ship-banner-wrapper reko-ship-wrapper"
          >

            <img
              src={reko8}
              alt="REKO Global Shipping"
              style={styles.shipImage}
              className="reko-ship-image"
            />

            <div style={styles.shipOverlay}>

              <FaShippingFast />

              <span>
                GLOBAL
                <br />
                SHIPPING
              </span>

            </div>

          </div>

        </section>


        {/* ====================================================
            CTA
        ==================================================== */}

        <section
          style={styles.ctaSection}
          className="cta-section reko-reveal"
        >

          <div>

            <span style={styles.ctaEyebrow}>
              READY TO PARTNER WITH US?
            </span>

            <h2 style={styles.ctaTitle}>

              LET'S BUILD THE
              <br />

              <span>
                PERFECT SPORTS PRODUCT.
              </span>

            </h2>

            <p style={styles.ctaText}>

              From customized designs and private-label
              programs to reliable bulk production,
              REKO Sports is ready to transform your
              product vision into dependable sports goods.

            </p>

          </div>


          <button
            style={styles.ctaButton}
            className="cta-button"
            onClick={() => navigate('/')}
          >

            EXPLORE ASHRAF GROUP

            <FaArrowRight />

          </button>

        </section>


        {/* ====================================================
            FOOTER
        ==================================================== */}

        <footer
          style={styles.footer}
          className="reko-footer reko-reveal"
        >

          <div style={styles.footerNumber}>
            12
          </div>


          <div style={styles.footerBrand}>

            <strong style={styles.footerBrandTitle}>
              REKO SPORTS
            </strong>

            <span style={styles.footerBrandSub}>
              A DIVISION OF ASHRAF GROUP OF INDUSTRIES
            </span>

          </div>


          <div
            style={styles.footerPillars}
            className="footer-pillars"
          >

            <Pillar
              icon={<FaTrophy />}
              text={
                <>
                  PASSION
                  <br />
                  FOR SPORTS
                </>
              }
            />

            <Pillar
              icon={<FaBullseye />}
              text={
                <>
                  FOCUS ON
                  <br />
                  PERFORMANCE
                </>
              }
            />

            <Pillar
              icon={<FaGlobeAmericas />}
              text={
                <>
                  COMMITTED TO
                  <br />
                  EXCELLENCE
                </>
              }
            />

          </div>

        </footer>


        {/* ====================================================
            FOOTER BOTTOM
        ==================================================== */}

        <div
          style={styles.footerBottom}
          className="footer-bottom"
        >

          <span>
            © {new Date().getFullYear()} REKO Sports
          </span>

          <span>
            Building Legacy. Powering the Future.
          </span>

        </div>


        {/* ====================================================
            MOVE TO OTHER SECTORS
            FULL WIDTH
        ==================================================== */}

        <section
          style={styles.sectorNavigation}
          className="sector-navigation reko-reveal"
        >

          <div
            style={styles.sectorNavigationInner}
            className="sector-navigation-inner"
          >

            <div>

              <span style={styles.sectorEyebrow}>
                ASHRAF GROUP OF INDUSTRIES
              </span>

              <h2
                style={styles.sectorTitle}
                className="sector-title"
              >

                MOVE TO OTHER
                {' '}

                <span
                  style={{
                    color: '#8fd0bd'
                  }}
                >
                  SECTORS
                </span>

              </h2>

              <p
                style={styles.sectorText}
                className="sector-text"
              >

                Explore our other business divisions and
                discover the complete range of products,
                services, manufacturing capabilities and
                industrial expertise offered by Ashraf Group
                of Industries.

              </p>

            </div>


            <button
              style={styles.sectorButton}
              className="sector-button"
              onClick={() => navigate('/')}
            >

              <span>
                MOVE TO OTHER SECTORS
              </span>

              <FaArrowRight />

            </button>

          </div>

        </section>

      </main>

    </div>

  );

}


/* ============================================================
   BADGE
   ============================================================ */

function Badge({
  icon,
  top,
  bottom
}) {

  return (

    <div style={styles.badgeItem}>

      <div
        style={styles.badgeIconCircle}
        className="badge-icon-circle reko-icon"
      >
        {icon}
      </div>

      <span style={styles.badgeText}>

        {top}
        <br />
        {bottom}

      </span>

    </div>

  );

}


/* ============================================================
   COLLAGE
   ============================================================ */

function Collage({
  className,
  style,
  image,
  alt
}) {

  return (

    <div
      style={style}
      className={`${className} reko-collage`}
    >

      <img
        src={image}
        alt={alt}
        style={styles.collageImage}
      />

    </div>

  );

}


/* ============================================================
   STAT
   ============================================================ */

function Stat({
  icon,
  number,
  label,
  sub
}) {

  return (

    <div
      style={styles.statCard}
      className="reko-stat-card"
    >

      <div style={styles.statIcon}>
        {icon}
      </div>

      <div style={styles.statNumber}>
        {number}
      </div>

      <div style={styles.statText}>

        <strong>
          {label}
        </strong>

        <span>
          {sub}
        </span>

      </div>

    </div>

  );

}


/* ============================================================
   SECTION TITLE
   ============================================================ */

function SectionTitle({
  eyebrow,
  title,
  accent,
  centered = false
}) {

  return (

    <div
      style={{
        ...styles.sectionTitleWrapper,
        textAlign:
          centered ? 'center' : 'left',
        alignItems:
          centered ? 'center' : 'flex-start'
      }}
    >

      <span style={styles.sectionEyebrow}>
        {eyebrow}
      </span>

      <h2 style={styles.sectionTitle}>

        {title}
        {' '}

        <span style={styles.sectionTitleAccent}>
          {accent}
        </span>

      </h2>

      <div style={styles.sectionLine} />

    </div>

  );

}


/* ============================================================
   PRODUCT CARD
   ============================================================ */

function ProductCard({
  icon,
  title,
  description
}) {

  return (

    <div
      style={styles.productCard}
      className="reko-product-card"
    >

      <div
        style={styles.productIcon}
        className="reko-product-icon"
      >
        {icon}
      </div>

      <div>

        <h3 style={styles.productTitle}>
          {title}
        </h3>

        <p style={styles.productDescription}>
          {description}
        </p>

      </div>

      <FaArrowRight
        style={styles.productArrow}
      />

    </div>

  );

}


/* ============================================================
   STRENGTH
   ============================================================ */

function Strength({
  icon,
  title,
  sub
}) {

  return (

    <div
      style={styles.strengthCard}
      className="reko-strength-card strength-card"
    >

      <div
        style={styles.strengthIcon}
        className="strength-icon"
      >
        {icon}
      </div>

      <strong>
        {title}
      </strong>

      <span>
        {sub}
      </span>

    </div>

  );

}


/* ============================================================
   CAPABILITY
   ============================================================ */

function Capability({
  icon,
  text
}) {

  return (

    <div
      style={styles.capabilityRow}
      className="reko-capability-row"
    >

      <div style={styles.capabilityIcon}>
        {icon}
      </div>

      <span>
        {text}
      </span>

      <FaCheck
        style={styles.capabilityCheck}
      />

    </div>

  );

}


/* ============================================================
   MARKET
   ============================================================ */

function Market({
  text
}) {

  return (

    <div style={styles.marketItem}>

      <FaCheckCircle />

      <span>
        {text}
      </span>

    </div>

  );

}


/* ============================================================
   FOOTER PILLAR
   ============================================================ */

function Pillar({
  icon,
  text
}) {

  return (

    <div
      style={styles.pillar}
      className="reko-pillar pillar-item"
    >

      <div style={styles.pillarIcon}>
        {icon}
      </div>

      <span>
        {text}
      </span>

    </div>

  );

}


/* ============================================================
   STYLES
   ============================================================ */

const styles = {


  /* ============================================================
     PAGE
  ============================================================ */

  page: {
    width: '100%',
    minHeight: '100vh',
    margin: 0,
    padding: 0,
    fontFamily:
      '"Helvetica Neue", Arial, sans-serif',
    color: '#20282b'
  },


  /* ============================================================
     TOP NAV
  ============================================================ */

  topNav: {
    width: '100%',
    margin: 0,
    padding: '18px 5%',
    background: '#11181a'
  },


  backButton: {
    background:
      'linear-gradient(135deg,#0c4a38,#087050)',
    color: '#fff',
    border: 'none',
    padding: '11px 18px',
    borderRadius: '7px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: '800',
    letterSpacing: '.2px'
  },


  /* ============================================================
     POSTER
  ============================================================ */

  posterCard: {
    width: '100%',
    maxWidth: 'none',
    minHeight: '100vh',
    margin: 0,
    background: '#fff',
    borderRadius: 0,
    boxShadow: 'none',
    overflow: 'hidden',
    padding: '35px 5%'
  },


  /* ============================================================
     HERO
  ============================================================ */

  heroSection: {
    display: 'flex',
    width: '100%',
    minHeight: '460px',
    marginBottom: '28px',
    paddingBottom: '28px',
    borderBottom: '1px solid #e8eeee'
  },


  heroLeft: {
    width: '45%',
    paddingRight: '35px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },


  logoArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '5px'
  },


  logoImage: {
    width: '175px',
    height: 'auto',
    objectFit: 'contain',
    display: 'block'
  },


  brandText: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '2px'
  },


  brandName: {
    fontSize: '25px',
    fontWeight: '950',
    letterSpacing: '1px',
    color: '#0c4a38'
  },


  brandTagline: {
    fontSize: '7px',
    fontWeight: '800',
    letterSpacing: '1.1px',
    color: '#879395',
    marginTop: '3px'
  },


  heroTitleBox: {
    margin: '28px 0 22px'
  },


  heroEyebrow: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    fontSize: '8px',
    fontWeight: '900',
    letterSpacing: '1.8px',
    color: '#0c4a38',
    marginBottom: '10px'
  },


  heroEyebrowLine: {
    width: '27px',
    height: '2px',
    background: '#0c4a38'
  },


  heroHeadline: {
    fontSize: '43px',
    fontWeight: '950',
    lineHeight: '1.01',
    color: '#172124',
    margin: 0,
    letterSpacing: '-1.5px'
  },


  heroDescription: {
    maxWidth: '610px',
    fontSize: '11px',
    color: '#637174',
    lineHeight: '1.7',
    marginTop: '14px',
    marginBottom: '0',
    fontWeight: '500'
  },


  heroBadgesGrid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(4,1fr)',
    gap: '9px',
    marginTop: '12px'
  },


  badgeItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center'
  },


  badgeIconCircle: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border:
      '1.5px solid #0c4a38',
    color: '#0c4a38',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    marginBottom: '5px',
    background: '#f4faf7'
  },


  badgeText: {
    fontSize: '7px',
    fontWeight: '900',
    color: '#354246',
    lineHeight: '1.15',
    letterSpacing: '.3px'
  },


  /* ============================================================
     HERO RIGHT
  ============================================================ */

  heroRight: {
    width: '55%',
    position: 'relative',
    minHeight: '430px',
    borderRadius: '9px',
    overflow: 'hidden',
    background: '#0d1717'
  },


  heroImageFrame: {
    position: 'absolute',
    inset: 0,
    clipPath:
      'polygon(15% 0,100% 0,100% 100%,0% 100%)',
    overflow: 'hidden'
  },


  heroMainImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },


  topRightCollage: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    width: '155px',
    height: '96px',
    border:
      '3px solid rgba(255,255,255,.95)',
    borderRadius: '6px',
    overflow: 'hidden',
    boxShadow:
      '0 8px 20px rgba(0,0,0,.28)'
  },


  midRightCollage: {
    position: 'absolute',
    top: '118px',
    right: '15px',
    width: '155px',
    height: '96px',
    border:
      '3px solid rgba(255,255,255,.95)',
    borderRadius: '6px',
    overflow: 'hidden',
    boxShadow:
      '0 8px 20px rgba(0,0,0,.28)'
  },


  botRightCollage: {
    position: 'absolute',
    top: '221px',
    right: '15px',
    width: '155px',
    height: '96px',
    border:
      '3px solid rgba(255,255,255,.95)',
    borderRadius: '6px',
    overflow: 'hidden',
    boxShadow:
      '0 8px 20px rgba(0,0,0,.28)'
  },


  collageImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },


  greenBanner: {
    position: 'absolute',
    bottom: 0,
    left: '10%',
    right: 0,
    background:
      'linear-gradient(90deg,#073c2f,#0c4a38,#087050)',
    color: '#fff',
    padding: '13px 22px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    clipPath:
      'polygon(5% 0,100% 0,100% 100%,0% 100%)'
  },


  bannerSmall: {
    fontSize: '8px',
    letterSpacing: '1.5px',
    color: '#b9d4cc',
    fontWeight: '800'
  },


  bannerLarge: {
    fontSize: '13px',
    letterSpacing: '1px',
    marginTop: '2px'
  },


  heroCornerLabel: {
    position: 'absolute',
    left: '20px',
    top: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    padding: '7px 10px',
    background:
      'rgba(7,25,22,.75)',
    backdropFilter: 'blur(8px)',
    color: '#fff',
    border:
      '1px solid rgba(255,255,255,.18)',
    borderRadius: '5px',
    fontSize: '7px',
    fontWeight: '900',
    letterSpacing: '.8px'
  },


  /* ============================================================
     STATS
  ============================================================ */

  statsSection: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(4,1fr)',
    gap: '12px',
    marginBottom: '35px'
  },


  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    border:
      '1px solid #e5ece9',
    borderRadius: '8px',
    padding: '13px 12px',
    background:
      'linear-gradient(135deg,#fff,#f8fbfa)'
  },


  statIcon: {
    width: '35px',
    height: '35px',
    borderRadius: '50%',
    background: '#eaf5f0',
    color: '#0c4a38',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    flexShrink: 0
  },


  statNumber: {
    fontSize: '16px',
    fontWeight: '950',
    color: '#0c4a38'
  },


  statText: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: '1.1',
    fontSize: '7px',
    letterSpacing: '.5px'
  },


  /* ============================================================
     TWO COLUMN
  ============================================================ */

  twoColumnSection: {
    display: 'flex',
    width: '100%',
    gap: '35px',
    marginBottom: '35px'
  },


  aboutColumn: {
    width: '42%'
  },


  productRangeColumn: {
    width: '58%'
  },


  /* ============================================================
     SECTION TITLE
  ============================================================ */

  sectionTitleWrapper: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '18px'
  },


  sectionEyebrow: {
    fontSize: '7px',
    fontWeight: '900',
    letterSpacing: '1.8px',
    color: '#7c898b',
    marginBottom: '5px'
  },


  sectionTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '950',
    letterSpacing: '-.3px',
    color: '#172124'
  },


  sectionTitleAccent: {
    color: '#0c4a38'
  },


  sectionLine: {
    width: '45px',
    height: '3px',
    background:
      'linear-gradient(90deg,#0c4a38,#6a9c8c)',
    marginTop: '8px',
    borderRadius: '5px'
  },


  /* ============================================================
     ABOUT
  ============================================================ */

  paragraphText: {
    fontSize: '10.5px',
    color: '#637174',
    lineHeight: '1.7',
    marginBottom: '12px'
  },


  missionBox: {
    display: 'flex',
    gap: '11px',
    padding: '13px',
    background:
      'linear-gradient(135deg,#eef8f4,#f8fbfa)',
    borderLeft:
      '3px solid #0c4a38',
    borderRadius: '5px',
    marginTop: '17px'
  },


  missionIcon: {
    width: '30px',
    height: '30px',
    background: '#0c4a38',
    color: '#fff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: '12px'
  },


  missionTitle: {
    fontSize: '8px',
    color: '#0c4a38',
    letterSpacing: '1px'
  },


  missionText: {
    fontSize: '9.5px',
    color: '#5e6c6f',
    lineHeight: '1.5',
    margin: '3px 0 0'
  },


  aboutHighlights: {
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    marginTop: '15px',
    fontSize: '8.5px',
    fontWeight: '800',
    color: '#435154'
  },


  aboutHighlight: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },


  /* ============================================================
     PRODUCTS
  ============================================================ */

  productArea: {
    display: 'flex',
    gap: '14px',
    width: '100%'
  },


  productListContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },


  productCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '9px',
    border:
      '1px solid #e6eeeb',
    borderRadius: '7px',
    background: '#fff'
  },


  productIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '7px',
    background: '#edf7f3',
    color: '#0c4a38',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    flexShrink: 0
  },


  productTitle: {
    margin: 0,
    fontSize: '9px',
    fontWeight: '950',
    color: '#20282b',
    letterSpacing: '.4px'
  },


  productDescription: {
    margin: '3px 0 0',
    fontSize: '7.8px',
    color: '#778487',
    lineHeight: '1.4'
  },


  productArrow: {
    marginLeft: 'auto',
    color: '#a7b6b2',
    fontSize: '8px',
    flexShrink: 0
  },


  productImageBox: {
    position: 'relative',
    width: '200px',
    height: '230px',
    borderRadius: '8px',
    overflow: 'hidden',
    background: '#15211f',
    flexShrink: 0
  },


  productImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },


  productImageOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    background:
      'linear-gradient(transparent,rgba(0,0,0,.78))',
    color: '#fff'
  },


  /* ============================================================
     STRENGTH
  ============================================================ */

  strengthSection: {
    padding: '27px 18px',
    background:
      'linear-gradient(135deg,#f5faf8,#fff)',
    border:
      '1px solid #e7efec',
    borderRadius: '9px',
    marginBottom: '35px'
  },


  strengthsGrid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(5,1fr)',
    gap: '12px',
    marginTop: '20px'
  },


  strengthCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '14px 5px',
    borderRadius: '7px',
    background: '#fff'
  },


  strengthIcon: {
    width: '42px',
    height: '42px',
    borderRadius: '50%',
    border:
      '1.5px solid #0c4a38',
    color: '#0c4a38',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    marginBottom: '8px',
    background: '#f2f9f6'
  },


  /* ============================================================
     CAPABILITIES
  ============================================================ */

  capabilitiesColumn: {
    width: '52%'
  },


  factoryColumn: {
    width: '48%',
    display: 'flex',
    alignItems: 'stretch'
  },


  capabilitiesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '7px'
  },


  capabilityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    padding: '9px',
    borderRadius: '6px',
    background: '#fff',
    border:
      '1px solid #edf2f0'
  },


  capabilityIcon: {
    width: '30px',
    height: '30px',
    borderRadius: '6px',
    background: '#edf7f3',
    color: '#0c4a38',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    flexShrink: 0
  },


  capabilityCheck: {
    marginLeft: 'auto',
    color: '#0c4a38',
    fontSize: '10px'
  },


  capabilityBottomNote: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '14px',
    padding: '11px',
    borderRadius: '6px',
    background: '#f4faf7',
    color: '#52716a',
    fontSize: '8.5px',
    fontWeight: '700'
  },


  factoryImageWrapper: {
    width: '100%',
    minHeight: '290px',
    borderRadius: '9px',
    overflow: 'hidden',
    position: 'relative',
    background: '#10201c'
  },


  factoryImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },


  factoryOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    background:
      'linear-gradient(transparent,rgba(0,0,0,.82))',
    color: '#fff'
  },


  /* ============================================================
     WORLDWIDE
  ============================================================ */

  worldwideSection: {
    display: 'grid',
    gridTemplateColumns:
      '1.1fr 1.5fr 1fr 1.2fr',
    gap: '16px',
    alignItems: 'center',
    padding: '25px 0',
    borderTop:
      '1px solid #e7efec',
    borderBottom:
      '1px solid #e7efec',
    marginBottom: '30px'
  },


  worldIntro: {
    paddingRight: '5px'
  },


  worldEyebrow: {
    fontSize: '7px',
    color: '#78878a',
    fontWeight: '900',
    letterSpacing: '1.5px',
    marginBottom: '6px'
  },


  worldTitle: {
    fontSize: '24px',
    lineHeight: '1',
    fontWeight: '950',
    color: '#162022',
    margin: 0
  },


  worldText: {
    fontSize: '8.8px',
    lineHeight: '1.6',
    color: '#6d7b7e',
    margin: '10px 0'
  },


  globalBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '6px 9px',
    borderRadius: '20px',
    background: '#eaf5f0',
    color: '#0c4a38',
    fontSize: '7px',
    fontWeight: '900'
  },


  worldMapContainer: {
    height: '140px',
    overflow: 'hidden'
  },


  worldMapImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },


  marketsColumn: {
    fontSize: '8px'
  },


  marketHeading: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    color: '#172124',
    fontSize: '8px',
    fontWeight: '950',
    marginBottom: '10px'
  },


  marketGrid: {
    display: 'grid',
    gridTemplateColumns:
      '1fr 1fr',
    gap: '7px'
  },


  marketItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    color: '#536164',
    fontSize: '7px',
    fontWeight: '800'
  },


  shipBanner: {
    height: '140px',
    borderRadius: '8px',
    overflow: 'hidden',
    position: 'relative',
    background: '#15211f'
  },


  shipImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },


  shipOverlay: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    padding: '11px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: '100%',
    color: '#fff',
    background:
      'linear-gradient(transparent,rgba(0,0,0,.8))',
    fontSize: '7px',
    fontWeight: '950',
    letterSpacing: '.8px'
  },


  /* ============================================================
     CTA
  ============================================================ */

  ctaSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '25px',
    padding: '28px',
    marginBottom: '30px',
    borderRadius: '9px',
    background:
      'linear-gradient(120deg,#073c2f,#0c4a38,#087050)',
    color: '#fff',
    position: 'relative',
    overflow: 'hidden'
  },


  ctaEyebrow: {
    fontSize: '7px',
    fontWeight: '900',
    letterSpacing: '1.7px',
    color: '#a9d1c5'
  },


  ctaTitle: {
    margin: '7px 0',
    fontSize: '25px',
    lineHeight: '1.05',
    fontWeight: '950'
  },


  ctaText: {
    margin: 0,
    maxWidth: '650px',
    color: '#c5dad4',
    fontSize: '9px',
    lineHeight: '1.6'
  },


  ctaButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    border: 'none',
    borderRadius: '6px',
    background: '#fff',
    color: '#0c4a38',
    padding: '13px 17px',
    fontSize: '8px',
    fontWeight: '950',
    letterSpacing: '.6px',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },


  /* ============================================================
     FOOTER
  ============================================================ */

  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '18px',
    paddingTop: '10px'
  },


  footerNumber: {
    background: '#0c4a38',
    color: '#fff',
    fontSize: '25px',
    fontWeight: '950',
    padding: '5px 13px',
    borderRadius: '4px'
  },


  footerBrand: {
    display: 'flex',
    flexDirection: 'column'
  },


  footerBrandTitle: {
    fontSize: '13px',
    fontWeight: '950',
    color: '#1a2427',
    letterSpacing: '.6px'
  },


  footerBrandSub: {
    fontSize: '8px',
    color: '#7b898c',
    marginTop: '2px'
  },


  footerPillars: {
    display: 'flex',
    gap: '22px'
  },


  pillar: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '7px',
    lineHeight: '1.15',
    fontWeight: '900',
    color: '#0c4a38'
  },


  pillarIcon: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: '#edf7f3',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px'
  },


  footerBottom: {
    marginTop: '20px',
    paddingTop: '11px',
    borderTop:
      '1px solid #edf1ef',
    display: 'flex',
    justifyContent: 'space-between',
    color: '#9aa5a7',
    fontSize: '7px'
  },


  /* ============================================================
     MOVE TO OTHER SECTORS
  ============================================================ */

  sectorNavigation: {
    width: 'calc(100% + 10.526%)',
    marginLeft: '-5.263%',
    marginRight: '-5.263%',
    marginTop: '35px',
    padding: '48px 5.263%',
    background:
      'linear-gradient(135deg,#061b15,#0c4a38,#087050)',
    color: '#fff'
  },


  sectorNavigationInner: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '35px'
  },


  sectorEyebrow: {
    display: 'block',
    fontSize: '8px',
    fontWeight: '900',
    letterSpacing: '2px',
    color: '#a9d1c5',
    marginBottom: '8px'
  },


  sectorTitle: {
    margin: 0,
    fontSize: '30px',
    lineHeight: '1.05',
    fontWeight: '950',
    letterSpacing: '-.6px'
  },


  sectorText: {
    maxWidth: '700px',
    margin: '11px 0 0',
    fontSize: '10px',
    lineHeight: '1.65',
    color: '#c5dad4'
  },


  sectorButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    minWidth: '235px',
    padding: '15px 20px',
    border: 'none',
    borderRadius: '6px',
    background: '#fff',
    color: '#0c4a38',
    fontSize: '9px',
    fontWeight: '950',
    letterSpacing: '.7px',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  }

};